<template>

    <Dialog :visible="visible" modal :header="createUpdateSeries" :style="{ width: '100rem' }" @update:visible="toggleVisibility">
        <div class="grid">
            <div class="form-field col-6">
                <div class="grid">
                    <div class="form-field col-12 mb-0">
                        <label for="label">Label</label>
                        <InputText maxlength = "255" id="label" v-model="seriesLabel" class="flex-auto" autocomplete="off"  placeholder="Enter series label"/>
                    </div>
                    <!-- Remove RGB color input fields (will add back later if needed) -->
                </div>
            </div>
            <div class="form-field col-6">
                <div class="border-500 surface-overlay border-1 border-round m-2 flex align-items-center justify-content-center" style="min-width: 200px; min-height: 100px">
                    <div v-if="!datasourceDetailsLoading">
                        <div v-if="datasourceId !== null &&  datasourceId > 0">
                            Datasource selected: <br />
                            id: <b>{{ datasourceId }}</b> <br />
                            code: <b>{{ datasourceCode }}</b> <br />
                            description: <b>{{ datasourceDescription }}</b> <br />
                        </div>
                        <div v-else>
                            Please, select a Datasource using the list provided.
                        </div>
                    </div>
                    <div v-else>
                        <DefaultLoader/>
                    </div>


                </div>
            </div>

            <div class="form-field col-3 mb-0">
                <label for="operation">Operation</label>
                <Dropdown inputId="type" id="operation" v-model="seriesOperation" :options="getOperations()" optionLabel="name" optionValue="value" placeholder="Select an option" :showClear="true"/>
            </div>

            <div class="form-field col-3 mb-0">
                <label for="assessment_code">Assessment Code</label>
                <Dropdown v-model="seriesAssessmentCode" :options="assessments" filter optionLabel="name" optionValue="code" placeholder="Select an assessment" class="w-full md:w-14rem" :showClear="true" />
            </div>

            <div class="form-field col-3 mb-0">
                <label for="attribute_code">Attribute Code</label>
                <Dropdown  id="attribute_code" v-model="seriesAttributeCode" :options="attributes" filter optionLabel="name" optionValue="code" placeholder="Select an attribute" class="w-full md:w-14rem" :showClear="true" />
            </div>

            <div class="form-field col-3 mb-0">
                <label for="tag_id">Tag</label>

                <Dropdown  id="tag_id" v-model="seriesTagId" :options="tags" filter optionLabel="description" optionValue="id" placeholder="Select a Tag" class="w-full md:w-14rem" :showClear="true" />

            </div>

            <div class="col-12">
                <DataTable
                    selection-mode="single"
                    :selection="selectedDatasource"
                    :value="datasources"
                    :lazy="true"
                    paginator
                    :rows="5"
                    ref="dt"
                    @page="onPage"
                    @rowSelect="selectDatasource"
                    selectionMode="single"
                    :rowClass="rowClass"
                    :rowStyle="rowStyle"
                    :loading="loading"
                    dataKey="id"
                    :totalRecords="totalDatasourceRecords"
                    responsiveLayout="scroll"
                    :globalFilterFields="['code', 'description']"
                    paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
                >
                    <template #empty> No Datasource available. </template>
                    <template #footer> Please, select a Datasource using the list above. When editing, the selected Datasource is highlighted but could not be in the current page.</template>
                    <Column field="id" header="Datasource ID"></Column>
                    <Column field="code" header="Code"></Column>
                    <Column field="description" header="Description"></Column>
                </DataTable>
            </div>
        </div>

        <div class="col-12">
            <div class="flex justify-content-between">
                <div class="m-2">
                    Please note that submitting a change will cause the Metric to be saved!
                </div>

                <div class="flex justify-content-end">
                    <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="onCancel" />
                    <Button label="Submit" icon="pi pi-check" class="ml-2 p-button-primary" @click="onSubmit" />
                </div>
            </div>
        </div>
    </Dialog>
</template>

<script setup>

import { computed, defineEmits, defineProps, ref, watch } from 'vue';
import GlobalRequest from '@/api/global-request';
import store from '@/store';
import { showToast } from '@/helpers/toast-handler';

const props = defineProps({
    display: {
        type: Boolean,
        required: false,
        default: false
    },
    seriesData: {
        type: Object,
        required: true
    },
    sharable: {
        type: Number
    },
    assessments: {
        type: Array,
        default: () => {return []}
    },
    attributes: {
        type: Array,
        default: () => {return []}
    },
    tags: {
        type: Array,
        default: () => {return []}
    }
});

const emit = defineEmits(['hideMetricSeriesPopup', 'saveMetricSeriesData']);
const visible = computed(() => props.display)
const series = computed(() => props.seriesData)
const loading = ref(false);
const totalDatasourceRecords = ref(0);
const datasources = ref([]);
const datasourceId = ref(series.value.datasource_id);
const datasourceCode = ref('');
const datasourceDescription = ref('');
const selectedDatasource = ref([]);

const seriesLabel = ref(null);
const seriesColorRed = ref(null);
const seriesColorGreen = ref(null);
const seriesColorBlue = ref(null);
const seriesOperation = ref(null);
const seriesAssessmentCode = ref(null);
const seriesAttributeCode = ref(null);
const seriesTagId = ref(null);

const createUpdateSeries = computed( () => {
    if (series.value && series.value.id !== null) {
        return 'Edit of a series';
    } else {
        return 'Create a new series';
    }
});

const datasourceDetailsLoading = ref(false);

/**
 * Called when there is a change in the props (so the user clicks on a different series)
 */
watch(props, (newValue/*, oldValue*/) => {

    /**
     * Fetch data only when the popup became visible
     */
    if (newValue.display === true) {
        seriesLabel.value = series.value.label;
        seriesColorRed.value = series.value.red;
        seriesColorGreen.value = series.value.green;
        seriesColorBlue.value = series.value.blue;
        seriesOperation.value = series.value.operation;
        seriesAssessmentCode.value = series.value.assessment_code;
        seriesAttributeCode.value = series.value.attribute_code;
        seriesTagId.value = series.value.tag_id;

        loadDatasourceData(1);

        /**
         * Fetch the data from the selected datasource only if it was selected using props,
         * not the "add new series" button.
         */
        if (props.seriesData.datasource_id > 0) {
            datasourceDetailsLoading.value = true;
            datasourceId.value = props.seriesData.datasource_id;

            //console.log('datasources.value', datasources.value);

            GlobalRequest.getRequest('/v2/datasource/'+datasourceId.value, {
                params: {
                    company_id: company_id.value
                },
            }).then((response) => {
                if (response.statusCode === 200) {
                    //console.log('response datasource details', response);

                    datasourceCode.value = response.data.code;
                    datasourceDescription.value = response.data.description;

                    datasourceDetailsLoading.value = false;
                }
            });
        } else {
            datasourceId.value = null;
        }
    }
})

function onCancel() {
    toggleVisibility(false);
}

function onSubmit() {

    if (datasourceId.value == null || datasourceId.value <=0 ) {
        showToast({}, "Please select a Datasource")
    } else if (seriesOperation.value == null || seriesOperation.value.length === 0 ) {
        showToast({}, "Please select an Operation")
    } else if (seriesLabel.value == null || seriesLabel.value.length === 0 ) {
        showToast({}, "Please select a Label")
    } else {
        emit('saveMetricSeriesData', {
            id: series.value.id,
            label: seriesLabel.value,
            red: seriesColorRed.value,
            green: seriesColorGreen.value,
            blue: seriesColorBlue.value,
            operation: seriesOperation.value,
            assessment_code: seriesAssessmentCode.value,
            attribute_code: seriesAttributeCode.value,
            tag_id: seriesTagId.value,
            datasource_id: datasourceId.value,
        });

        toggleVisibility(false);
    }
}

function getOperations() {
    return [
        { name: 'Average value', value: 'AVG' },
        { name: 'Min value', value: 'MIN' },
        { name: 'Max value', value: 'MAX' },
        { name: 'Count user', value: 'COUNT_USERS' },
        { name: 'Count submitted forms', value: 'COUNT_FORMS' },
        { name: 'Sum value', value: 'SUM' },
    ];
}

function toggleVisibility(visible) {
    emit('hideMetricSeriesPopup', visible)
}

const company_id = computed(() => {
    return store.getters['auth/userCompanyId']?.id;
});

const rowClass = (data) => {
    return [{ 'bg-primary': data.id === datasourceId.value }];
};

const rowStyle = (data) => {
    if (data.id === datasourceId.value) {
        return { fontWeight: 'bold', fontStyle: 'italic' };
    }
};

function selectDatasource(selectedRow)
{
    if (selectedRow) {
        datasourceId.value = selectedRow.data.id;
        datasourceCode.value = selectedRow.data.code;
        datasourceDescription.value = selectedRow.data.description;
    }
    //console.log('selectDatasource', selectedRow);
    //console.log('selectedDatasource', selectedDatasource.value);
}

function onPage(event) {
    //console.log('onPage', event.page)
    loadDatasourceData(event.page + 1);
}

function loadDatasourceData(page) {

    //console.log('sharable', props.sharable);

    let sharableFilter = 'non-shareable';
    if (props.sharable === 1) {
        sharableFilter = 'shareable';
    }

    loading.value = true;
    GlobalRequest.getRequest('/v2/datasource', {
        params: {
            shareable: sharableFilter,
            user_group: 'non_user_group',
            company_id: company_id.value,
            paginate: true,
            page_size: 5,
            page: page,
        },
    }).then((response) => {
        if (response.statusCode === 200) {
            //console.log('response datasource', response.data);

            loading.value = false;
            datasources.value = response.data.data;

            totalDatasourceRecords.value = response.data.total;
        }
    });
}

</script>

<style scoped>
.form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
}

.form-field label {
    margin-bottom: 0.5rem;
}
</style>